/* FullProjectEditor — 2026-02 Case Study editor overhaul.
 *
 * Unified Studio editor for a single case study. Opens either on "New case
 * study" (after a one-click empty-draft create) or on "Edit case study" from
 * a project card. Reuses existing media widgets (CoverCropModal,
 * VideoUploadModal, GalleryModal) as pop-overs; adds SEO & Sharing section
 * with slug / SEO title / Meta description / OG image / per-gallery Alt text.
 *
 * Autosaves text fields on blur via PATCH /projects/{slug}. Never auto-
 * publishes — publish is only reachable through the explicit Publish button.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ArrowLeft, Film, Image as ImageIcon, Images, ArrowUp, ArrowDown, Trash2, Eye, Rocket } from "lucide-react";
import { CoverCropModal, GalleryModal, VideoUploadModal, DraftPreviewLink } from "@/pages/studio/CaseStudyMedia";
import { studioHeaders } from "@/lib/studioAuth";
import { CATEGORIES as CANONICAL_CATEGORIES, CATEGORY_IDS, labelFor as categoryLabelFor, normalizeLegacy } from "@/lib/projectCategories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026-02 iter75 — single source of truth for case study categories lives
// in /app/frontend/src/lib/projectCategories.js. Studio dropdown + public
// Work filter + card badge all read from it so the two lists cannot drift
// again (which is what caused the 2026-02 category mismatch bug).
const SERVICE_OPTIONS = [
    "Creative Direction",
    "Brand Film Production",
    "Campaign Production",
    "Product Video Production",
    "Social Reels Production",
    "Food Photography",
    "Menu Photography",
];

function slugify(s) {
    return (s || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
}

const inputCls =
    "mt-2 h-11 bg-transparent border border-border rounded-xl px-3 focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50";
const taCls =
    "mt-2 bg-transparent border border-border rounded-xl focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50";

export default function FullProjectEditor({ slug: initialSlug, onBack, onChanged }) {
    const [slug, setSlug] = useState(initialSlug);
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPoster, setShowPoster] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [showGallery, setShowGallery] = useState(false);
    const [posterFile, setPosterFile] = useState(null);
    const posterInputRef = useRef(null);
    const [seoOpen, setSeoOpen] = useState(false);
    const [versionsOpen, setVersionsOpen] = useState(false);
    const [versions, setVersions] = useState([]);

    const load = useCallback(async ({ background = false } = {}) => {
        // 2026-10-08 scroll-jump fix — a background refresh after onBlur-patch
        // must NOT swap the whole editor to the "Loading…" placeholder,
        // otherwise every input unmounts and the page scrolls back to the top
        // mid-form. Only the very first load (project === null) is allowed to
        // flip the big loading flag.
        if (!background) setLoading(true);
        try {
            const r = await axios.get(`${API}/studio/projects/${slug}/preview`, { headers: studioHeaders() });
            setProject(r.data);
        } catch (err) {
            toast.error("Failed to load draft", { description: err?.response?.data?.detail || err.message });
        } finally {
            if (!background) setLoading(false);
        }
    }, [slug]);

    useEffect(() => { load(); }, [load]);

    const patch = async (fields, opts = {}) => {
        // 2026-10-08 — optimistic merge so the user keeps typing without the
        // UI jumping while the PATCH + background refresh completes.
        setProject((prev) => (prev ? { ...prev, ...fields } : prev));
        setSaving(true);
        try {
            const r = await axios.patch(`${API}/projects/${slug}`, fields, { headers: studioHeaders() });
            if (r.data?.slug && r.data.slug !== slug) {
                setSlug(r.data.slug);
            }
            if (!opts.silent) toast.success("Saved");
            await load({ background: true });
            onChanged && onChanged();
        } catch (err) {
            toast.error("Save failed", { description: err?.response?.data?.detail || err.message });
            // Revert optimistic merge on failure by forcing a fresh fetch.
            await load({ background: true });
        } finally {
            setSaving(false);
        }
    };

    const onPosterPick = (e) => {
        const f = e.target.files?.[0];
        if (f) setPosterFile(f);
    };

    const uploadGalleryItem = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            await axios.post(`${API}/projects/${slug}/gallery-upload`, fd, { headers: studioHeaders() });
            toast.success("Gallery image added");
            await load();
        } catch (err) {
            toast.error("Gallery upload failed", { description: err?.response?.data?.detail || err.message });
        }
    };

    const moveGallery = async (i, dir) => {
        const g = [...(project.gallery || [])];
        const alt = [...(project.gallery_alt || [])];
        const cap = [...(project.gallery_caption || [])];
        const cre = [...(project.gallery_credit || [])];
        const rig = [...(project.gallery_rights || [])];
        const j = i + dir;
        if (j < 0 || j >= g.length) return;
        [g[i], g[j]] = [g[j], g[i]];
        [alt[i], alt[j]] = [alt[j] || "", alt[i] || ""];
        [cap[i], cap[j]] = [cap[j] || "", cap[i] || ""];
        [cre[i], cre[j]] = [cre[j] || "", cre[i] || ""];
        [rig[i], rig[j]] = [rig[j] || "", rig[i] || ""];
        await patch({ gallery: g, gallery_alt: alt, gallery_caption: cap, gallery_credit: cre, gallery_rights: rig }, { silent: true });
    };
    const removeGallery = async (i) => {
        if (!window.confirm("Remove this gallery image?")) return;
        const g = [...(project.gallery || [])];
        const alt = [...(project.gallery_alt || [])];
        const cap = [...(project.gallery_caption || [])];
        const cre = [...(project.gallery_credit || [])];
        const rig = [...(project.gallery_rights || [])];
        g.splice(i, 1); alt.splice(i, 1); cap.splice(i, 1); cre.splice(i, 1); rig.splice(i, 1);
        await patch({ gallery: g, gallery_alt: alt, gallery_caption: cap, gallery_credit: cre, gallery_rights: rig }, { silent: true });
    };
    const updateAlt = async (i, value) => {
        const alt = [...(project.gallery_alt || [])];
        while (alt.length < (project.gallery || []).length) alt.push("");
        alt[i] = value;
        await patch({ gallery_alt: alt }, { silent: true });
    };
    const updateMediaField = async (field, i, value) => {
        const cur = [...(project[field] || [])];
        while (cur.length < (project.gallery || []).length) cur.push("");
        cur[i] = value;
        await patch({ [field]: cur }, { silent: true });
    };

    const toggleService = async (s) => {
        const cur = project.services || [];
        const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
        await patch({ services: next }, { silent: true });
    };

    const onPublish = async () => {
        if (!window.confirm("发布到公开页面?发布后 draft 媒体会被复制到公开桶,不能单击撤回(需要 Unpublish 并手动恢复 URL)。")) return;
        await patch({ published: true });
    };

    // 2026-02 Phase 2d — version history fetch / restore
    const loadVersions = async () => {
        try {
            const r = await axios.get(`${API}/projects/${slug}/versions`, { headers: studioHeaders() });
            setVersions(r.data || []);
        } catch (err) {
            toast.error("Failed to load version history", { description: err?.response?.data?.detail || err.message });
        }
    };
    const restoreVersion = async (vId, snapAt) => {
        if (!window.confirm(`Restore to the version from ${snapAt.slice(0, 19)}? The restored version will be a DRAFT — review and Publish when ready. The current state will be snapshotted so you can undo.`)) return;
        try {
            await axios.post(`${API}/projects/${slug}/versions/${vId}/restore`, null, { headers: studioHeaders() });
            toast.success("Restored — reloaded as draft");
            await loadVersions();
            await load();
        } catch (err) {
            toast.error("Restore failed", { description: err?.response?.data?.detail || err.message });
        }
    };

    if (loading || !project) {
        return <div className="py-16 text-center text-cafe-mocha" data-testid="editor-loading">Loading editor…</div>;
    }

    const published = !!project.published;
    const resolvedPoster = project.video_poster_resolved || project.video_poster;
    const resolvedVideo = project.video_file_url_resolved || project.video_file_url;
    const draftSlug = `/work/${project.slug}`;

    return (
        <div className="bg-cafe-latte border border-border rounded-[2rem] p-6 md:p-10 mb-10" data-testid="full-project-editor">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-border">
                <button onClick={onBack} data-testid="editor-back" className="inline-flex items-center gap-2 text-cafe-mocha hover:text-cafe-espresso">
                    <ArrowLeft className="h-4 w-4" /> Back to all case studies
                </button>
                <div className="flex items-center gap-2">
                    <span className={cn("text-xs px-2 py-0.5 rounded-full",
                        published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}
                        data-testid="editor-status">
                        {published ? "Live" : "Private draft"}
                    </span>
                    {!published && <DraftPreviewLink project={{ slug }} headers={studioHeaders()} />}
                    {!published && (
                        <Button onClick={onPublish} data-testid="editor-publish"
                            className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-10 px-5">
                            <Rocket className="mr-2 h-4 w-4" /> Publish
                        </Button>
                    )}
                </div>
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Title *</Label>
                    <Input data-testid="editor-title" defaultValue={project.title} onBlur={(e) => e.target.value !== project.title && patch({ title: e.target.value })} className={inputCls} />
                </div>
                <div>
                    <Label className="text-cafe-espresso font-medium">Client / brand name</Label>
                    <Input data-testid="editor-client" defaultValue={project.client_name || ""} onBlur={(e) => e.target.value !== (project.client_name || "") && patch({ client_name: e.target.value || null }, { silent: true })} className={inputCls} />
                </div>
                <div>
                    <Label className="text-cafe-espresso font-medium">Category *</Label>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button type="button" data-testid="editor-category-trigger"
                                className="mt-2 h-11 w-full bg-transparent border border-border rounded-xl px-3 text-left text-cafe-espresso hover:border-cafe-olive">
                                {CATEGORY_IDS.includes(project.category)
                                    ? categoryLabelFor(project.category, "en")
                                    : (<span className="text-amber-700">legacy: {project.category || "— set a category —"}</span>)}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-cafe-cream border-border rounded-xl w-56">
                            {CANONICAL_CATEGORIES.map((c) => (
                                <DropdownMenuItem key={c.id} data-testid={`category-opt-${c.id}`}
                                                  onClick={() => patch({ category: c.id }, { silent: true })}
                                                  className="cursor-pointer">
                                    <span className="font-medium">{c.en}</span>
                                    <span className="text-cafe-espresso/50 ml-2 text-xs">· {c.cn}</span>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    {!CATEGORY_IDS.includes(project.category) && project.category && (
                        <p className="mt-1.5 text-[11.5px] text-amber-700" data-testid="category-legacy-hint">
                            旧分类 "{project.category}" 需要重新归类。{(() => {
                                const n = normalizeLegacy(project.category);
                                return n.confident && n.id ? ` 建议: ${categoryLabelFor(n.id, "en")}` : "";
                            })()}
                        </p>
                    )}
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Services provided (multi-select)</Label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="editor-services">
                        {SERVICE_OPTIONS.map((s) => {
                            const on = (project.services || []).includes(s);
                            return (
                                <button type="button" key={s} onClick={() => toggleService(s)}
                                    data-testid={`service-${slugify(s)}`}
                                    className={cn("h-9 px-4 rounded-full border text-sm transition-all",
                                        on ? "bg-cafe-olive text-cafe-cream border-cafe-olive" : "border-border text-cafe-espresso hover:border-cafe-olive")}>
                                    {s}
                                </button>
                            );
                        })}
                        {(project.services || [])
                            .filter((s) => !SERVICE_OPTIONS.includes(s))
                            .map((s) => (
                                <button type="button" key={s} onClick={() => toggleService(s)}
                                    data-testid={`service-legacy-${slugify(s)}`}
                                    className="h-9 px-4 rounded-full bg-cafe-mocha/15 text-cafe-mocha border border-dashed border-cafe-mocha/30 italic text-sm">
                                    legacy: {s} ×
                                </button>
                            ))}
                    </div>
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Deliverables (one per line — real quantities & formats, e.g. "1 brand film + 7 commercial videos")</Label>
                    <Textarea data-testid="editor-deliverables" rows={3}
                        defaultValue={(project.deliverables || []).join("\n")}
                        onBlur={(e) => {
                            const parsed = e.target.value.split(/\n+/).map((x) => x.trim()).filter(Boolean);
                            const cur = (project.deliverables || []).join("\n");
                            if (e.target.value !== cur) patch({ deliverables: parsed }, { silent: true });
                        }}
                        placeholder={"1 Brand Film (60s + 15s cutdown)\n7 Social Reels (vertical 9:16)\n30 edited stills"}
                        className={taCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Story / description (public — real customer-approved copy)</Label>
                    <Textarea data-testid="editor-description" rows={6}
                        defaultValue={project.description || ""}
                        onBlur={(e) => e.target.value !== (project.description || "") && patch({ description: e.target.value }, { silent: true })}
                        placeholder="Opening concept, brand objective, how the shoot went…"
                        className={taCls} />
                </div>

                {/* Phase 6 — Director case-study scaffold.
                    Each field is optional; the public page only renders filled sections. */}
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Work classification</Label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="editor-work-type">
                        {[
                            ["client", "Client work"],
                            ["spec", "Spec work"],
                            ["personal", "Personal work"],
                            ["collaboration", "Collaboration"],
                        ].map(([val, lbl]) => {
                            const on = (project.work_type || "client") === val;
                            return (
                                <button type="button" key={val} data-testid={`editor-work-type-${val}`}
                                    onClick={() => patch({ work_type: val }, { silent: true })}
                                    className={cn("h-9 px-4 rounded-full border text-sm transition-all",
                                        on ? "bg-cafe-olive text-cafe-cream border-cafe-olive" : "border-border text-cafe-espresso hover:border-cafe-olive")}>
                                    {lbl}
                                </button>
                            );
                        })}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 items-center">
                        <label className="inline-flex items-center gap-2 text-sm text-cafe-espresso cursor-pointer">
                            <input type="checkbox" data-testid="editor-in-progress"
                                defaultChecked={!!project.in_progress}
                                onChange={(e) => patch({ in_progress: e.target.checked }, { silent: true })} />
                            In progress (not final)
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-cafe-espresso cursor-pointer">
                            <input type="checkbox" data-testid="editor-featured"
                                defaultChecked={!!project.featured}
                                onChange={(e) => patch({ featured: e.target.checked }, { silent: true })} />
                            Feature on home (Selected Work · max 3)
                        </label>
                    </div>
                </div>

                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">One-line concept (shown under the title)</Label>
                    <Input data-testid="editor-concept" defaultValue={project.concept || ""}
                        onBlur={(e) => e.target.value !== (project.concept || "") && patch({ concept: e.target.value || null }, { silent: true })}
                        placeholder="e.g. A quiet rush — rebuilt the brand's morning ritual on film." className={inputCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Brand objective (what the client needed to achieve)</Label>
                    <Textarea data-testid="editor-objective" rows={3}
                        defaultValue={project.objective || ""}
                        onBlur={(e) => e.target.value !== (project.objective || "") && patch({ objective: e.target.value || null }, { silent: true })}
                        placeholder="Open the second outlet with a visual identity that works on IG, Google and in-store menu."
                        className={taCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Creative direction (treatment, references, visual decisions)</Label>
                    <Textarea data-testid="editor-creative-direction" rows={4}
                        defaultValue={project.creative_direction || ""}
                        onBlur={(e) => e.target.value !== (project.creative_direction || "") && patch({ creative_direction: e.target.value || null }, { silent: true })}
                        placeholder="Warm backlight · hand-ritual close-ups · one-wide anchor · Casta Fine Serif captions…"
                        className={taCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">My role & key decisions (what Kaily specifically did)</Label>
                    <Textarea data-testid="editor-kaily-role" rows={4}
                        defaultValue={project.kaily_role || ""}
                        onBlur={(e) => e.target.value !== (project.kaily_role || "") && patch({ kaily_role: e.target.value || null }, { silent: true })}
                        placeholder="Director + DOP + colourist. Pushed back on original brief to shoot handheld opening; locked 50mm as the brand's prime lens."
                        className={taCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Client challenge (optional — what made this tricky)</Label>
                    <Textarea data-testid="editor-challenge" rows={3}
                        defaultValue={project.challenge || ""}
                        onBlur={(e) => e.target.value !== (project.challenge || "") && patch({ challenge: e.target.value || null }, { silent: true })}
                        placeholder="Narrow space, half-day window, no artificial lighting allowed."
                        className={taCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Distribution & verified results (what actually ran — be factual)</Label>
                    <Textarea data-testid="editor-distribution" rows={3}
                        defaultValue={project.distribution || ""}
                        onBlur={(e) => e.target.value !== (project.distribution || "") && patch({ distribution: e.target.value || null }, { silent: true })}
                        placeholder="IG grid rollout week of 12 Feb · in-store menu print · Google Business cover. If no verifiable numbers, leave this blank."
                        className={taCls} />
                </div>
                <div>
                    <Label className="text-cafe-espresso font-medium">Shot date</Label>
                    <Input data-testid="editor-date" defaultValue={project.shot_date || ""}
                        onBlur={(e) => e.target.value !== (project.shot_date || "") && patch({ shot_date: e.target.value || null }, { silent: true })}
                        placeholder="Jan 2026" className={inputCls} />
                </div>
                <div>
                    <Label className="text-cafe-espresso font-medium">Location</Label>
                    <Input data-testid="editor-location" defaultValue={project.location || ""}
                        onBlur={(e) => e.target.value !== (project.location || "") && patch({ location: e.target.value || null }, { silent: true })}
                        placeholder="Petaling Jaya, MY" className={inputCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Instagram post URL (optional)</Label>
                    <Input data-testid="editor-ig" defaultValue={project.instagram_url || ""}
                        onBlur={(e) => e.target.value !== (project.instagram_url || "") && patch({ instagram_url: e.target.value || null }, { silent: true })}
                        placeholder="https://www.instagram.com/p/…/" className={inputCls} />
                </div>
            </div>

            {/* Media */}
            <div className="mt-10 pt-8 border-t border-border">
                <h3 className="font-serif text-2xl text-cafe-espresso mb-4">Media</h3>

                {/* Hero Film */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-cafe-cream rounded-2xl p-5 border border-border">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-cafe-espresso font-medium"><Film className="h-4 w-4" /> Hero Film</div>
                            <Button variant="outline" size="sm" onClick={() => setShowVideo(true)} data-testid="editor-upload-film"
                                className="rounded-full border-cafe-olive text-cafe-olive hover:bg-cafe-olive/10">
                                {project.video_file_url ? "Replace" : "Upload MP4"}
                            </Button>
                        </div>
                        {resolvedVideo ? (
                            <video src={resolvedVideo} poster={resolvedPoster || undefined} controls
                                className="w-full h-40 object-cover rounded-xl bg-black" data-testid="editor-film-preview" />
                        ) : (
                            <div className="h-40 flex items-center justify-center text-xs text-cafe-mocha/60 border border-dashed border-border rounded-xl">
                                No Hero Film uploaded yet
                            </div>
                        )}
                    </div>

                    {/* Poster / Cover */}
                    <div className="bg-cafe-cream rounded-2xl p-5 border border-border">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-cafe-espresso font-medium"><ImageIcon className="h-4 w-4" /> Poster / Cover image</div>
                            <input type="file" accept="image/*" ref={posterInputRef} onChange={onPosterPick} className="hidden" data-testid="editor-poster-input" />
                            <Button variant="outline" size="sm" onClick={() => posterInputRef.current?.click()} data-testid="editor-upload-poster"
                                className="rounded-full border-cafe-olive text-cafe-olive hover:bg-cafe-olive/10">
                                {project.hero_image && !(project.hero_image || "").includes("placeholder") ? "Replace" : "Upload"}
                            </Button>
                        </div>
                        {project.hero_image && !(project.hero_image || "").includes("placeholder") ? (
                            <img src={project.hero_image} alt="cover" className="w-full h-40 object-cover rounded-xl" data-testid="editor-poster-preview" />
                        ) : (
                            <div className="h-40 flex items-center justify-center text-xs text-cafe-mocha/60 border border-dashed border-border rounded-xl">
                                No cover image yet — required before publishing
                            </div>
                        )}
                    </div>
                </div>

                {/* Gallery */}
                <div className="bg-cafe-cream rounded-2xl p-5 border border-border">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 text-cafe-espresso font-medium"><Images className="h-4 w-4" /> Gallery ({(project.gallery || []).length})</div>
                        <Button variant="outline" size="sm" onClick={() => setShowGallery(true)} data-testid="editor-manage-gallery"
                            className="rounded-full border-cafe-olive text-cafe-olive hover:bg-cafe-olive/10">
                            Upload images
                        </Button>
                    </div>
                    {(project.gallery || []).length === 0 ? (
                        <div className="py-6 text-center text-xs text-cafe-mocha/60">No gallery images yet — click Upload images.</div>
                    ) : (
                        <ul className="space-y-3" data-testid="editor-gallery-list">
                            {(project.gallery || []).map((url, i) => (
                                <li key={url + i} className="flex items-start gap-3 p-2 rounded-xl border border-border bg-cafe-latte/50" data-testid={`editor-gallery-item-${i}`}>
                                    <img src={url} alt={(project.gallery_alt && project.gallery_alt[i]) || ""} className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <Input placeholder="Alt text — describe the actual visual (no keyword stuffing)"
                                            defaultValue={(project.gallery_alt && project.gallery_alt[i]) || ""}
                                            onBlur={(e) => e.target.value !== ((project.gallery_alt && project.gallery_alt[i]) || "") && updateAlt(i, e.target.value)}
                                            data-testid={`editor-gallery-alt-${i}`} className={cn(inputCls, "mt-0 h-9")} />
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 mt-1.5">
                                            <Input placeholder="Caption (shown under image)"
                                                defaultValue={(project.gallery_caption && project.gallery_caption[i]) || ""}
                                                onBlur={(e) => e.target.value !== ((project.gallery_caption && project.gallery_caption[i]) || "") && updateMediaField("gallery_caption", i, e.target.value)}
                                                data-testid={`editor-gallery-caption-${i}`} className={cn(inputCls, "mt-0 h-8 text-xs")} />
                                            <Input placeholder="Credit (e.g. Photo: Kaily)"
                                                defaultValue={(project.gallery_credit && project.gallery_credit[i]) || ""}
                                                onBlur={(e) => e.target.value !== ((project.gallery_credit && project.gallery_credit[i]) || "") && updateMediaField("gallery_credit", i, e.target.value)}
                                                data-testid={`editor-gallery-credit-${i}`} className={cn(inputCls, "mt-0 h-8 text-xs")} />
                                            <Input placeholder="Rights (e.g. Client-owned)"
                                                defaultValue={(project.gallery_rights && project.gallery_rights[i]) || ""}
                                                onBlur={(e) => e.target.value !== ((project.gallery_rights && project.gallery_rights[i]) || "") && updateMediaField("gallery_rights", i, e.target.value)}
                                                data-testid={`editor-gallery-rights-${i}`} className={cn(inputCls, "mt-0 h-8 text-xs")} />
                                        </div>
                                        <div className="mt-1 text-[10px] text-cafe-mocha/60 truncate">#{i + 1} · {url.split("/").pop()}</div>
                                    </div>
                                    <div className="flex flex-col gap-1 flex-shrink-0">
                                        <button onClick={() => moveGallery(i, -1)} disabled={i === 0}
                                            data-testid={`editor-gallery-up-${i}`}
                                            className="p-1.5 rounded-full border border-border hover:border-cafe-olive disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => moveGallery(i, +1)} disabled={i === (project.gallery || []).length - 1}
                                            data-testid={`editor-gallery-down-${i}`}
                                            className="p-1.5 rounded-full border border-border hover:border-cafe-olive disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => removeGallery(i)} data-testid={`editor-gallery-remove-${i}`}
                                            className="p-1.5 rounded-full border border-red-200 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* SEO & Sharing */}
            <div className="mt-10 pt-8 border-t border-border">
                <button onClick={() => setSeoOpen((v) => !v)} data-testid="editor-seo-toggle"
                    className="w-full flex items-center justify-between text-left">
                    <h3 className="font-serif text-2xl text-cafe-espresso">SEO & Sharing</h3>
                    <span className="text-sm text-cafe-mocha">{seoOpen ? "Hide ▲" : "Show ▼"}</span>
                </button>
                {seoOpen && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="editor-seo-section">
                        <div className="md:col-span-2">
                            <Label className="text-cafe-espresso font-medium">URL slug {published && <span className="text-xs text-cafe-mocha/60">(locked — unpublish to rename)</span>}</Label>
                            <Input data-testid="editor-slug" defaultValue={project.slug}
                                disabled={published}
                                onBlur={(e) => {
                                    const v = slugify(e.target.value);
                                    if (v && v !== project.slug) patch({ slug: v });
                                    else if (v !== e.target.value) e.target.value = v;
                                }}
                                className={inputCls} />
                            <div className="text-xs text-cafe-mocha/60 mt-1">Public URL: <code>{(typeof window !== "undefined" ? window.location.origin : "")}{draftSlug}</code></div>
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-cafe-espresso font-medium">SEO title</Label>
                            <Input data-testid="editor-seo-title" defaultValue={project.seo_title || ""}
                                onBlur={(e) => e.target.value !== (project.seo_title || "") && patch({ seo_title: e.target.value || null }, { silent: true })}
                                placeholder={`${project.client_name ? project.client_name + " · " : ""}${project.category || "Brand Film"} — Kaily F&B Visual Studio`} className={inputCls} />
                            <div className="text-xs text-cafe-mocha/60 mt-1">Leave blank to use the auto default above (client + category + Kaily).</div>
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-cafe-espresso font-medium">Meta description</Label>
                            <Textarea data-testid="editor-meta-desc" rows={3}
                                defaultValue={project.meta_description || ""}
                                onBlur={(e) => e.target.value !== (project.meta_description || "") && patch({ meta_description: e.target.value || null }, { silent: true })}
                                placeholder={(project.description || "").slice(0, 160) || "A one-line real description of this project (max 320 chars)."}
                                className={taCls} />
                            <div className="text-xs text-cafe-mocha/60 mt-1">Leave blank to auto-use the first 240 chars of the Story / description above.</div>
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-cafe-espresso font-medium">OG / social sharing image URL</Label>
                            <Input data-testid="editor-og-image" defaultValue={project.og_image || ""}
                                onBlur={(e) => e.target.value !== (project.og_image || "") && patch({ og_image: e.target.value || null }, { silent: true })}
                                placeholder={project.video_poster || project.hero_image || "https://…/share.jpg"} className={inputCls} />
                            <div className="text-xs text-cafe-mocha/60 mt-1">Leave blank to auto-use the Poster (preferred) or Cover image. After publish, use a stable public URL.</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Version History (2026-02 Phase 2d) */}
            <div className="mt-10 pt-8 border-t border-border">
                <button onClick={() => { setVersionsOpen((v) => { if (!v) loadVersions(); return !v; }); }}
                    data-testid="editor-versions-toggle"
                    className="w-full flex items-center justify-between text-left">
                    <h3 className="font-serif text-2xl text-cafe-espresso">Version history</h3>
                    <span className="text-sm text-cafe-mocha">{versionsOpen ? "Hide ▲" : "Show ▼"}</span>
                </button>
                {versionsOpen && (
                    <div className="mt-4 space-y-2" data-testid="editor-versions-list">
                        {versions.length === 0 ? (
                            <p className="text-sm text-cafe-mocha/60 italic">No prior versions yet — edits start producing snapshots the next time you change any field.</p>
                        ) : (
                            versions.map((v, i) => (
                                <div key={v.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border bg-cafe-cream" data-testid={`version-row-${i}`}>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-cafe-espresso">
                                            {v.snapshot_at ? new Date(v.snapshot_at).toLocaleString() : "—"}
                                        </div>
                                        <div className="text-xs text-cafe-mocha/70 truncate">
                                            Changed: {(v.changed_fields || []).join(", ") || "—"}
                                        </div>
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => restoreVersion(v.id, v.snapshot_at)}
                                        disabled={published}
                                        data-testid={`version-restore-${i}`}
                                        className="rounded-full border-cafe-olive text-cafe-olive hover:bg-cafe-olive/10 flex-shrink-0">
                                        Restore as draft
                                    </Button>
                                </div>
                            ))
                        )}
                        {published && versions.length > 0 && (
                            <p className="text-xs text-cafe-mocha/60 italic mt-2">Unpublish the case study first to enable Restore.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="mt-10 pt-8 border-t border-border flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-cafe-mocha">{saving ? "Saving…" : "Autosaved on field blur."}</div>
                <div className="flex items-center gap-2">
                    {!published && <DraftPreviewLink project={{ slug }} headers={studioHeaders()} />}
                    <Button variant="outline" onClick={onBack} data-testid="editor-close"
                        className="rounded-full h-10 px-5 border-cafe-olive text-cafe-olive hover:bg-cafe-olive/10">
                        <Eye className="mr-2 h-4 w-4" /> Save & close
                    </Button>
                    {!published && (
                        <Button onClick={onPublish} data-testid="editor-publish-footer"
                            className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-10 px-5">
                            <Rocket className="mr-2 h-4 w-4" /> Publish
                        </Button>
                    )}
                </div>
            </div>

            {/* Modals (reuse existing media widgets) */}
            {posterFile && (
                <CoverCropModal file={posterFile} slug={slug} headers={studioHeaders()}
                    onDone={() => { setPosterFile(null); load(); }}
                    onCancel={() => setPosterFile(null)} />
            )}
            {showVideo && (
                <VideoUploadModal project={{ slug, video_file_url: project.video_file_url, video_poster: project.video_poster }}
                    headers={studioHeaders()} onClose={() => setShowVideo(false)}
                    onChanged={() => { setShowVideo(false); load(); }} />
            )}
            {showGallery && (
                <GalleryModal project={{ slug, gallery: project.gallery || [] }} headers={studioHeaders()}
                    onClose={() => setShowGallery(false)}
                    onChanged={() => { setShowGallery(false); load(); }} />
            )}
        </div>
    );
}
